import { Component, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type UserRole          = 'teacher' | 'student';
export type HomeworkStatus    = 'Draft' | 'Published' | 'Closed';
export type SubmissionStatus  = 'Assigned' | 'In Progress' | 'Submitted' | 'Late' | 'Graded' | 'Returned';
export type HomeworkTab       = 'assignments' | 'review' | 'my-homework' | 'inbox';
export type ViewMode          = 'list' | 'form' | 'detail';
export type HomeworkPriority  = 'Low' | 'Normal' | 'High';

export interface HomeworkAlert {
  id:           number;
  student_id:   string;
  homework_id:  number;
  title:        string;
  msg_text:     any;
  msg_time:     string;
  unread:       boolean;
  kind:         'assigned' | 'reminder' | 'graded' | 'returned' | 'updated';
  msg_tmtamp:   string;
}

export interface HomeworkDocument {
  name:    string;
  size:    number;
  type:    string;
  dataUrl: string;
}

export interface HomeworkAssignment {
  id:            number;
  title:         string;
  subject:       string;
  class_name:    string;
  section:       string;
  teacher_name:  string;
  assigned_date: string;
  due_date:      string;
  due_time:      string;
  max_marks:     number;
  instructions:  string;
  documents:     HomeworkDocument[];
  status:        HomeworkStatus;
  priority:      HomeworkPriority;
  notify_on_save:boolean;
  created_at:    string;
}

export interface StudentHomework {
  id:              number;
  homework_id:     number;
  student_id:      string;
  student_name:    string;
  roll_no:         string;
  class_name:      string;
  status:          SubmissionStatus;
  work_text:       string;
  file_name:       string;
  file:            HomeworkDocument | null;
  submitted_at:    any | null;
  marks_obtained:  number | null;
  teacher_remark:  string;
  student_remark:  string;
  last_updated:    any | null;
}

export interface RosterStudent {
  id:      string;
  name:    string;
  roll_no: string;
  class_name: string;
}

@Component({
  selector:    'app-homework',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './homework.html',
  styleUrl:    './homework.scss',
})
export class Homework {
  dataService     = inject(DataService);
  auth = inject(AuthService);
  loading = false;
  students: any = [];
  teachers: any = [];
  user: any = null;

  role              = signal<UserRole>('teacher');
  currentStudentId  = signal('');
  activeTab         = signal<HomeworkTab>('assignments');
  saveSuccess       = signal('');
  toastKind         = signal<'success' | 'danger'>('success');
  showDeleteModal   = signal(false);
  deleteCtx         = signal<{ id: number; label: string } | null>(null);

  // Assignment CRUD
  hwMode            = signal<ViewMode>('list');
  editingHwId       = signal<number | null>(null);
  hwSubmitted       = false;
  hwForm: Partial<HomeworkAssignment> = {};
  hwFormDocs        = signal<HomeworkDocument[]>([]);
  submitFile        = signal<HomeworkDocument | null>(null);

  // Filters (signals so computed lists stay in sync)
  hwSearch          = signal('');
  hwClassFilter     = signal('');
  hwSubjectFilter   = signal('');
  hwStatusFilter    = signal('');
  reviewHwId        = signal<number>(1);
  reviewStatus      = signal('');
  reviewSearch      = signal('');
  myStatusFilter    = signal('');
  mySearch          = signal('');

  // Student submit / teacher grade modals
  updatingWork      = signal<StudentHomework | null>(null);
  gradingWork       = signal<StudentHomework | null>(null);
  
  submitForm        = { work_text: '', student_remark: '' };
  readonly maxFileBytes = 8 * 1024 * 1024;
  readonly maxAssignmentDocs = 5;
  readonly acceptedFiles = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.zip';
  gradeForm         = { marks_obtained: 0 as number | null, teacher_remark: '', return_for_revision: false };
  submitAttempted   = false;
  gradeAttempted    = false;

  // ── LOOKUPS ──────────────────────────────────────────
  readonly classOptions = this.dataService.classes;
  readonly subjectOpts = this.dataService.subjects;
  readonly sectionOpts = this.dataService.sections;
  readonly priorities: HomeworkPriority[] = ['Low','Normal','High'];
  readonly hwStatuses: HomeworkStatus[] = ['Draft','Published','Closed'];
  readonly submissionStatuses: SubmissionStatus[] = [
    'Assigned','In Progress','Submitted','Late','Graded','Returned',
  ];

  roster: RosterStudent[] = [
    // { id:'S001', name:'Arjun Sharma',   roll_no:'11-A-01', class_name:'Class XI – Science' },
    // { id:'S002', name:'Priya Mehta',    roll_no:'11-A-02', class_name:'Class XI – Science' },
    // { id:'S006', name:'Kavita Yadav',   roll_no:'11-A-03', class_name:'Class XI – Science' },
    // { id:'S013', name:'Meena Sharma',   roll_no:'11-A-04', class_name:'Class XI – Science' },
    // { id:'S014', name:'Vikram Patel',   roll_no:'11-A-05', class_name:'Class XI – Science' },
    // { id:'S003', name:'Rahul Kumar',    roll_no:'9-A-01',  class_name:'Class IX' },
    // { id:'S005', name:'Vikram Singh',   roll_no:'9-A-02',  class_name:'Class IX' },
    // { id:'S010', name:'Suresh Babu',    roll_no:'9-A-03',  class_name:'Class IX' },
    // { id:'S020', name:'Pooja Devi',     roll_no:'9-A-04',  class_name:'Class IX' },
  ];

  private nextHwId     = 1;
  private nextSubId    = 1;
  private nextAlertId  = 1;
  private readonly today = new Date().toISOString().split('T')[0];

  assignments = signal<HomeworkAssignment[]>([]);       //(this.buildAssignments());
  submissions = signal<StudentHomework[]>([]);          //(this.buildSubmissions());
  alerts      = signal<HomeworkAlert[]>([]);            //(this.buildAlerts());

  // ── COMPUTED ─────────────────────────────────────────
  readonly currentStudent = computed(() =>
    this.roster?.find(s => s.id === this.currentStudentId()) ?? this.roster[0]
  );

  readonly filteredAssignments = computed(() => {
    const q = this.hwSearch().toLowerCase().trim();
    return this.assignments().filter(a =>
      (!q || a.title.toLowerCase().includes(q) || a.subject.toLowerCase().includes(q) || a.teacher_name.toLowerCase().includes(q)) &&
      (!this.hwClassFilter()   || a.class_name === this.hwClassFilter()) &&
      (!this.hwSubjectFilter() || a.subject    === this.hwSubjectFilter()) &&
      (!this.hwStatusFilter()  || a.status     === this.hwStatusFilter())
    ).sort((a, b) => b.assigned_date.localeCompare(a.assigned_date));
  });

  readonly reviewAssignment = computed(() =>
    this.assignments().find(a => a.id === this.reviewHwId())
  );

  readonly filteredSubmissions = computed(() => {
    const q = this.reviewSearch().toLowerCase().trim();
    return this.submissions()
      .filter(s => s.homework_id === this.reviewHwId())
      .filter(s => !this.reviewStatus() || s.status === this.reviewStatus())
      .filter(s => !q || s.student_name.toLowerCase().includes(q) || s.roll_no.toLowerCase().includes(q))
      .sort((a, b) => a.roll_no.localeCompare(b.roll_no));
  });

  readonly myHomework = computed(() => {
    const student = this.currentStudent();
    const q = this.mySearch().toLowerCase().trim();
    let rowZ = this.submissions();

    const rows = rowZ.filter(s => s.student_id.toString() === student.id)
      .map(s => ({ sub: s, hw: this.assignments().find(a => a.id === s.homework_id) }))
      .filter(x => x.hw && x.hw.status !== 'Draft');

    return rows
      .filter(x => !this.myStatusFilter() || this.effectiveStatus(x.sub, x.hw!) === this.myStatusFilter())
      .filter(x => !q || x.hw!.title.toLowerCase().includes(q) || x.hw!.subject.toLowerCase().includes(q))
      .sort((a, b) => a.hw!.due_date.localeCompare(b.hw!.due_date));
  });

  myAlerts = computed(() => {
    let retVal = this.alerts().filter(a => {
      const cur_sid = this.currentStudentId();
      return a.student_id.toString() === cur_sid;
    });

    return retVal;
  });

  readonly unreadAlertCount = computed(() => this.myAlerts().filter(a => a.unread).length);

  readonly teacherStats = computed(() => {
    const list = this.assignments();
    const subs = this.submissions();
    return {
      total:      list.length,
      published:  list.filter(a => a.status === 'Published').length,
      drafts:     list.filter(a => a.status === 'Draft').length,
      pending:    subs.filter(s => s.status === 'Submitted' || s.status === 'Late').length,
      graded:     subs.filter(s => s.status === 'Graded').length,
    };
  });

  readonly studentStats = computed(() => {
    const rows = this.myHomework();
    const overdue = rows.filter(x => this.effectiveStatus(x.sub, x.hw!) === 'Late' || this.isOverdue(x.hw!, x.sub)).length;
    return {
      assigned:   rows.filter(x => x.sub.status === 'Assigned').length,
      inProgress: rows.filter(x => x.sub.status === 'In Progress').length,
      submitted:  rows.filter(x => x.sub.status === 'Submitted' || x.sub.status === 'Late').length,
      overdue,
      graded:     rows.filter(x => x.sub.status === 'Graded').length,
    };
  });

  readonly reviewStats = computed(() => {
    const list = this.submissions().filter(s => s.homework_id === this.reviewHwId());
    if (!list.length) return { total:0, submitted:0, pending:0, graded:0, avg:0 };
    const submitted = list.filter(s => ['Submitted','Late','Graded'].includes(s.status)).length;
    const graded    = list.filter(s => s.status === 'Graded');
    const avg = graded.length
      ? Math.round(graded.reduce((sum, s) => sum + (s.marks_obtained ?? 0), 0) / graded.length)
      : 0;
    return {
      total: list.length,
      submitted,
      pending: list.filter(s => s.status === 'Assigned' || s.status === 'In Progress').length,
      graded: graded.length,
      avg,
    };
  });

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");

    if(erp_rem && !this.user) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
    }

    this.loading = true;
    await this.dataService.getAllRecords('admissions').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.students = res;
        this.currentStudentId.set(res[0].adm_code);

        let srostr: any = {};
        for(let i=0; i<res.length; i++) {
          srostr = { 
            id: res[i].adm_code,
            name: res[i].std_name, 
            roll_no: res[i].roll_no,
            class_name: res[i].class_name
          };

          this.roster.push(srostr);
        }
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('staff_tch').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.teachers = res;
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('hw_assignments').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          if(res[i].documents && res[i].documents != "{}") {
            res[i].documents = this.dataService.str2json(res[i].documents);
          }
        }
        this.assignments.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('hw_studentwork').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.submissions.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('hw_alerts').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.alerts.set(res);
      }
    });

  }

  // ── ROLE / TAB ───────────────────────────────────────
  switchRole(role: UserRole): void {
    this.role.set(role);
    this.hwMode.set('list');
    this.updatingWork.set(null);
    this.gradingWork.set(null);
    this.activeTab.set(role === 'teacher' ? 'assignments' : 'my-homework');
  }

  switchStudent(id: string): void {
    this.currentStudentId.set(id);
  }

  // ── ASSIGNMENT CRUD ──────────────────────────────────
  openNewAssignment(): void {
    this.hwForm = {
      title: '',
      subject: 'Mathematics',
      class_name: 'Class XI – Science',
      section: 'A',
      teacher_name: 'Rajesh Kumar',
      assigned_date: this.today,
      due_date: '',
      due_time: '23:59',
      max_marks: 20,
      instructions: '',
      documents: [],
      status: 'Published',
      priority: 'Normal',
      notify_on_save: true,
      created_at: this.today,
    };
    this.hwFormDocs.set([]);
    this.editingHwId.set(null);
    this.hwSubmitted = false;
    this.hwMode.set('form');
  }

  isEmpty(obj: any) {
    return obj == "{}" ? true : obj && Object.keys(obj).length === 0 && obj.constructor === Object;
  };

  editAssignment(a: HomeworkAssignment): void {
    this.hwForm = { ...a };
    if(!this.isEmpty(a.documents)) {
      a.documents = this.dataService.str2json(a.documents);
      this.hwFormDocs.set((a.documents ?? []).map(d => ({ ...d })));
    }
    this.editingHwId.set(a.id);
    this.hwSubmitted = false;
    this.hwMode.set('form');
  }

  viewAssignment(a: HomeworkAssignment): void {
    this.editingHwId.set(a.id);
    this.hwMode.set('detail');
  }

  saveAssignment(): void {
    this.hwSubmitted = true;
    const f = this.hwForm;
    if (!f.title?.trim() || !f.subject || !f.class_name || !f.due_date || !f.instructions?.trim()) return;

    const wasNew = this.editingHwId() === null;
    const prev   = wasNew ? null : this.assignments().find(a => a.id === this.editingHwId());
    const hw: HomeworkAssignment = {
      id:            this.editingHwId() ?? this.nextHwId++,
      title:         f.title!.trim(),
      subject:       f.subject!,
      class_name:    f.class_name!,
      section:       f.section ?? 'A',
      teacher_name:  f.teacher_name || '',
      assigned_date: f.assigned_date || this.today,
      due_date:      f.due_date!,
      due_time:      f.due_time || '12:00',
      max_marks:     f.max_marks ?? 20,
      instructions:  f.instructions!.trim(),
      documents:     this.hwFormDocs().map(d => ({ ...d })),
      status:        f.status ?? 'Published',
      priority:      f.priority ?? 'Normal',
      notify_on_save:f.notify_on_save ?? true,
      created_at:    prev?.created_at ?? this.today,
    };

    if (wasNew) {
      this.doSave('hw_assignments', hw);
      this.assignments.update(list => [hw, ...list]);

      if (hw.status === 'Published') {
        this.seedSubmissionsFor(hw);
        if (hw.notify_on_save) this.notifyClass(hw, 'assigned');
      }

      this.flash('Homework assigned. Students have been notified.');
    } else {
      this.doUpdate('hw_assignments', hw, false);
      this.assignments.update(list => list.map(a => a.id === hw.id ? hw : a));

      if (hw.status === 'Published' && prev?.status === 'Draft') {
        this.seedSubmissionsFor(hw);
        if (hw.notify_on_save) this.notifyClass(hw, 'assigned');
        this.flash('Homework published. Students have been informed.');
      } else {
        if (hw.notify_on_save && hw.status === 'Published') this.notifyClass(hw, 'updated');
        this.flash('Homework updated.');
      }
    }

    this.hwMode.set('list');
  }

  confirmDelete(id: number, label: string): void {
    this.deleteCtx.set({ id, label });
    this.showDeleteModal.set(true);
  }

  executeDelete(): void {
    const ctx = this.deleteCtx();
    if (!ctx) return;
    this.assignments.update(l => l.filter(a => a.id !== ctx.id));
    this.submissions.update(l => l.filter(s => s.homework_id !== ctx.id));
    this.alerts.update(l => l.filter(a => a.homework_id !== ctx.id));
    this.showDeleteModal.set(false);
    this.deleteCtx.set(null);
    this.hwMode.set('list');
    this.flash('Homework deleted.');
  }

  publishDraft(a: HomeworkAssignment): void {
    this.assignments.update(list => list.map(x => x.id === a.id ? { ...x, status: 'Published' } : x));
    this.assignments().filter((obj: any) => {
      if(obj.id == a.id) {
        this.doUpdate('hw_assignments', obj, false);
      }
    });

    this.seedSubmissionsFor({ ...a, status: 'Published' });
    this.doUpdate('hw_studentwork', { ...a, status: 'Published' }, false);

    this.notifyClass(a, 'assigned');
    this.flash('Published. Students in the class have been notified.');
  }

  closeAssignment(a: HomeworkAssignment): void {
    this.assignments.update(list => list.map(x => x.id === a.id ? { ...x, status: 'Closed' } : x));
    this.flash('Assignment closed. No further submissions will be accepted.');
  }

  sendReminder(a: HomeworkAssignment): void {
    const pending = this.submissions().filter(s =>
      s.homework_id === a.id && (s.status === 'Assigned' || s.status === 'In Progress')
    );

    pending.forEach(s => this.pushAlert(s.student_id, a, 'reminder',
      `Reminder: “${a.title}” is due on ${this.formatDate(a.due_date)} at ${this.formatTime(a.due_time)}.`));
    this.flash(`Reminder sent to ${pending.length} student${pending.length === 1 ? '' : 's'}.`);
  }

  // ── STUDENT UPDATE / SUBMIT ──────────────────────────
  openUpdate(sub: StudentHomework): void {
    this.submitForm = {
      work_text:      sub.work_text,
      student_remark: sub.student_remark,
    };
    this.submitFile.set(sub.file ? { ...sub.file } : null);
    this.submitAttempted = false;
    this.updatingWork.set({ ...sub });
  }

  markInProgress(sub: StudentHomework): void {
    this.patchSubmission(sub.id, {
      status: 'In Progress',
      last_updated: this.nowStamp(),
    });
    this.flash('Marked as in progress.');
  }

  saveStudentUpdate(submit: boolean): void {
    const sub = this.updatingWork();
    if (!sub) return;
    this.submitAttempted = true;
    if (submit && !this.submitForm.work_text.trim() && !this.submitFile()) return;

    const hw = this.assignments().find(a => a.id === sub.homework_id);
    const late = !!(hw && this.isPastDue(hw));
    const nextStatus: SubmissionStatus = submit ? (late ? 'Late' : 'Submitted') : 'In Progress';
    const file = this.submitFile();

    this.patchSubmission(sub.id, {
      status:          nextStatus,
      work_text:       this.submitForm.work_text.trim(),
      file,
      file_name:       file?.name ?? '',
      student_remark:  this.submitForm.student_remark.trim(),
      submitted_at:    submit ? this.nowStamp() : sub.submitted_at,
      last_updated:    this.nowStamp(),
    });
    this.updatingWork.set(null);
    this.flash(submit
      ? (late ? 'Submitted after the due date (marked late).' : 'Homework submitted successfully.')
      : 'Progress saved.');
  }

  // ── TEACHER GRADE ────────────────────────────────────
  openGrade(sub: StudentHomework): void {
    this.gradeForm = {
      marks_obtained: sub.marks_obtained,
      teacher_remark: sub.teacher_remark,
      return_for_revision: false,
    };
    this.gradeAttempted = false;
    this.gradingWork.set({ ...sub });
  }

  saveGrade(): void {
    const sub = this.gradingWork();
    if (!sub) return;
    this.gradeAttempted = true;
    const hw = this.assignments().find(a => a.id === sub.homework_id);
    const max = hw?.max_marks ?? 20;

    if (this.gradeForm.return_for_revision) {
      this.patchSubmission(sub.id, {
        status: 'Returned',
        teacher_remark: this.gradeForm.teacher_remark.trim() || 'Please revise and resubmit.',
        last_updated: this.nowStamp(),
      });
      if (hw) {
        this.pushAlert(sub.student_id, hw, 'returned',
          `“${hw.title}” was returned for revision. ${this.gradeForm.teacher_remark.trim() || 'Please update and resubmit.'}`);
      }
      this.gradingWork.set(null);
      this.flash(`Returned to ${sub.student_name} for revision.`);
      return;
    }

    const marks = this.gradeForm.marks_obtained;
    if (marks === null || marks === undefined || Number.isNaN(Number(marks))) return;
    if (Number(marks) < 0 || Number(marks) > max) return;

    this.patchSubmission(sub.id, {
      status: 'Graded',
      marks_obtained: Number(marks),
      teacher_remark: this.gradeForm.teacher_remark.trim(),
      last_updated: this.nowStamp(),
    });
    if (hw) {
      this.pushAlert(sub.student_id, hw, 'graded',
        `“${hw.title}” has been graded: ${marks}/${max}. ${this.gradeForm.teacher_remark.trim()}`.trim());
    }
    this.gradingWork.set(null);
    this.flash(`Graded ${sub.student_name}. Student has been notified.`);
  }

  // ── INBOX ────────────────────────────────────────────
  markAlertRead(id: number): void {
    if(!id) return;

    this.alerts.update(list => list.map(a => a.id === id ? { ...a, unread: false } : a));
    this.alerts()?.filter((obj: any) => {
      if(obj.id==id) {
        this.doUpdate('hw_alerts', obj, true);
      }
    });
  }

  markAllAlertsRead(): void {
    const sid = this.currentStudentId();
    this.alerts.update(list => list.map(a => a.student_id === sid ? { ...a, unread: false } : a));

    // this.alerts()?.filter((obj: any) => {
    //   if(obj.id==id) {
    //     this.doUpdate('hw_alerts', obj);
    //   }
    // });

    this.flash('All notifications marked as read.');
  }

  openHomeworkFromAlert(alert: HomeworkAlert): void {
    if(alert.unread) this.markAlertRead(alert.id);
    const sub = this.submissions().find(s => s.homework_id === alert.homework_id && s.student_id === alert.student_id);
    this.activeTab.set('my-homework');
    if (sub) this.openUpdate(sub);
  }

  // ── HELPERS ──────────────────────────────────────────
  getViewAssignment(): HomeworkAssignment | undefined {
    return this.assignments().find(a => a.id === this.editingHwId());
  }

  getHw(id: number): HomeworkAssignment | undefined {
    return this.assignments().find(a => a.id === id);
  }

  submissionCount(hwId: number, statuses?: SubmissionStatus[]): number {
    return this.submissions().filter(s =>
      s.homework_id === hwId && (!statuses || statuses.includes(s.status))
    ).length;
  }

  effectiveStatus(sub: StudentHomework, hw: HomeworkAssignment): SubmissionStatus {
    if (sub.status === 'Assigned' || sub.status === 'In Progress') {
      if (this.isPastDue(hw)) return 'Late';
    }
    return sub.status;
  }

  isOverdue(hw: HomeworkAssignment, sub: StudentHomework): boolean {
    return this.isPastDue(hw) && (sub.status === 'Assigned' || sub.status === 'In Progress');
  }

  isPastDue(hw: HomeworkAssignment): boolean {
    return `${hw.due_date}T${hw.due_time || '23:59'}` < `${this.today}T12:00`;
  }

  daysUntilDue(hw: HomeworkAssignment): number {
    const due = new Date(hw.due_date + 'T00:00:00');
    const now = new Date(this.today + 'T00:00:00');
    return Math.round((due.getTime() - now.getTime()) / 86400000);
  }

  dueLabel(hw: HomeworkAssignment): string {
    const d = this.daysUntilDue(hw);
    if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} overdue`;
    if (d === 0) return 'Due today';
    if (d === 1) return 'Due tomorrow';
    return `Due in ${d} days`;
  }

  getHwStatusClass(s: HomeworkStatus): string {
    return { Draft:'hs-draft', Published:'hs-published', Closed:'hs-closed' }[s] ?? '';
  }

  getSubStatusClass(s: SubmissionStatus): string {
    return {
      Assigned:'ss-assigned', 'In Progress':'ss-progress', Submitted:'ss-submitted',
      Late:'ss-late', Graded:'ss-graded', Returned:'ss-returned',
    }[s] ?? '';
  }

  getPriorityClass(p: HomeworkPriority): string {
    return { Low:'pr-low', Normal:'pr-normal', High:'pr-high' }[p] ?? '';
  }

  getSubjectColor(subject: string): string {
    const map: Record<string, string> = {
      Mathematics:'#1a3a6b', Physics:'#7c3aed', Chemistry:'#059669', Biology:'#0d9488',
      English:'#d97706', Hindi:'#dc2626', 'Computer Science':'#0284c7', History:'#be185d',
      Geography:'#0f766e', Economics:'#4338ca', 'Physical Education':'#ea580c', Accountancy:'#374151',
    };
    return map[subject] ?? '#1a3a6b';
  }

  getAlertIcon(kind: HomeworkAlert['kind']): string {
    return {
      assigned:'bi-journal-plus', reminder:'bi-bell-fill', graded:'bi-award-fill',
      returned:'bi-arrow-counterclockwise', updated:'bi-pencil-fill',
    }[kind];
  }

  getAlertBg(kind: HomeworkAlert['kind']): string {
    return {
      assigned:'bg-primary', reminder:'bg-warning', graded:'bg-success',
      returned:'bg-danger', updated:'bg-info',
    }[kind];
  }

  formatDate(s: string): string {
    if (!s) return '—';
    return new Date(s + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
  }

  formatTime(t: string): string {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hr = Number(h);
    const ap = hr >= 12 ? 'PM' : 'AM';
    return `${hr % 12 || 12}:${m} ${ap}`;
  }

  formatStamp(s: string): string {
    if (!s) return '—';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
  }

  resetHwFilters(): void {
    this.hwSearch.set('');
    this.hwClassFilter.set('');
    this.hwSubjectFilter.set('');
    this.hwStatusFilter.set('');
  }

  studentsForClass(className: string): RosterStudent[] {
    return this.roster.filter(s => s.class_name === className);
  }

  private patchSubmission(id: number, patch: Partial<StudentHomework>): void {
    this.submissions.update(list => list.map(s => s.id === id ? { ...s, ...patch } : s));
    this.submissions()?.filter((obj: any) => {
      if(obj.id == id) {
        this.doUpdate('hw_studentwork', obj, false);
      }
    });
  }

  private seedSubmissionsFor(hw: HomeworkAssignment): void {
    const existing = new Set(
      this.submissions().filter(s => s.homework_id === hw.id).map(s => s.student_id)
    );
    const fresh: StudentHomework[] = this.studentsForClass(hw.class_name)
      .filter(st => !existing.has(st.id))
      .map(st => ({
        id: this.nextSubId++,
        homework_id: hw.id,
        student_id: st.id,
        student_name: st.name,
        roll_no: st.roll_no,
        class_name: st.class_name,
        status: 'Assigned' as SubmissionStatus,
        work_text: '',
        file_name: '',
        file: null,
        submitted_at: null,
        marks_obtained: null,
        teacher_remark: '',
        student_remark: '',
        last_updated: null
      }));
        // last_updated: null      this.nowStamp()
    if (fresh.length) {
      let subData: any = Object.assign([], fresh);
      delete subData.id;
      this.doSave('gen_studentwork', subData);

      this.submissions.update(list => [...fresh, ...list]);
    }
  }

  private notifyClass(hw: HomeworkAssignment, kind: 'assigned' | 'updated'): void {
    const text = kind === 'assigned'
      ? `New homework assigned: “${hw.title}” (${hw.subject}) · due ${this.formatDate(hw.due_date)}.`
      : `Homework updated: “${hw.title}” · due ${this.formatDate(hw.due_date)}.`;
    this.studentsForClass(hw.class_name).forEach(st => this.pushAlert(st.id, hw, kind, text));
  }

  private pushAlert(studentId: string, hw: HomeworkAssignment, kind: HomeworkAlert['kind'], text: string): void {
    const newMsg: any = {
      id: this.nextAlertId++,
      student_id: studentId,
      homework_id: hw.id,
      title: hw.title,
      msg_text: text,
      msg_time: 'Just now',
      unread: true,
      kind,
      msg_tmtamp: this.today,
    };

    this.alerts.update(list => [newMsg, ...list]);
    this.doSave('hw_alerts', newMsg);
  }

  private nowStamp(): string {
    return new Date().toISOString();
  }

  private flash(msg: string, kind: 'success' | 'danger' = 'success'): void {
    this.toastKind.set(kind);
    this.saveSuccess.set(msg);
    setTimeout(() => this.saveSuccess.set(''), 2800);
  }

  // ── DOCUMENTS ────────────────────────────────────────
  onAssignmentFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';

    for (const file of files) {
      if (!this.validateFile(file)) continue;
      this.readFile(file, doc => {
        this.hwFormDocs.update(list => {
          if (list.length >= this.maxAssignmentDocs) {
            this.flash(`You can attach up to ${this.maxAssignmentDocs} documents.`, 'danger');
            return list;
          }
          return [...list, doc];
        });
      });
    }
  }

  removeAssignmentDoc(index: number): void {
    this.hwFormDocs.update(list => list.filter((_, i) => i !== index));
  }

  onStudentFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!this.validateFile(file)) return;
    this.readFile(file, doc => this.submitFile.set(doc));
  }

  removeStudentFile(): void {
    this.submitFile.set(null);
  }

  downloadDocument(doc: HomeworkDocument, event?: Event): void {
    event?.stopPropagation();
    const a = document.createElement('a');
    a.href = this.auth.svrUrl + '/uploads/' + this.user.erp_key + '/hwa/' + doc.name;
    a.target = '_blank';
    a.download = doc.name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  fileIcon(doc: HomeworkDocument | string | null | undefined): string {
    const name = typeof doc === 'string' ? doc : (doc?.name ?? '');
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    if (['pdf'].includes(ext)) return 'bi-file-earmark-pdf-fill text-danger';
    if (['doc', 'docx', 'rtf', 'txt'].includes(ext)) return 'bi-file-earmark-word-fill text-primary';
    if (['xls', 'xlsx'].includes(ext)) return 'bi-file-earmark-excel-fill text-success';
    if (['ppt', 'pptx'].includes(ext)) return 'bi-file-earmark-ppt-fill text-warning';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'bi-file-earmark-image-fill text-info';
    if (['zip', 'rar', '7z'].includes(ext)) return 'bi-file-earmark-zip-fill';
    return 'bi-file-earmark-fill';
  }

  private validateFile(file: File): boolean {
    if (file.size > this.maxFileBytes) {
      this.flash(`“${file.name}” is larger than 8 MB.`, 'danger');
      return false;
    }
    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
    const allowed = this.acceptedFiles.split(',');
    if (ext !== '.' && !allowed.includes(ext)) {
      this.flash(`“${file.name}” is not an allowed file type.`, 'danger');
      return false;
    }
    return true;
  }

  private readFile(file: File, done: (doc: HomeworkDocument) => void): void {
    const reader = new FileReader();
    reader.onload = () => {
      done({
        name:    file.name,
        size:    file.size,
        type:    file.type || 'application/octet-stream',
        dataUrl: reader.result as string,
      });
    };
    reader.onerror = () => this.flash(`Could not read “${file.name}”.`, 'danger');
    reader.readAsDataURL(file);
  }

  private makePdfDocument(filename: string, heading: string, paragraphs: string[]): HomeworkDocument {
    const ascii = (s: string) => s.replace(/[’‘]/g, "'").replace(/[“”]/g, '"').replace(/[–—]/g, '-');
    const esc = (s: string) => ascii(s).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
    const ops: string[] = ['BT', '/F1 16 Tf', '50 750 Td', `(${esc(heading)}) Tj`, '/F1 11 Tf'];
    paragraphs.forEach((p, i) => {
      const chunks = p.match(/.{1,88}(\s|$)/g)?.map(c => c.trim()).filter(Boolean) ?? [p];
      chunks.forEach((c, j) => {
        ops.push(i === 0 && j === 0 ? '0 -28 Td' : '0 -16 Td');
        ops.push(`(${esc(c)}) Tj`);
      });
    });
    ops.push('ET');
    const stream = ops.join('\n');

    const parts: string[] = [];
    const offsets: number[] = [0];
    const len = () => parts.reduce((n, p) => n + p.length, 0);
    const obj = (n: number, content: string) => {
      offsets[n] = len();
      parts.push(`${n} 0 obj\n${content}\nendobj\n`);
    };

    parts.push('%PDF-1.4\n');
    obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
    obj(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
    obj(3, '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>');
    obj(4, `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    obj(5, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
    const xrefPos = len();
    parts.push('xref\n0 6\n0000000000 65535 f \n');
    for (let i = 1; i <= 5; i++) {
      parts.push(`${String(offsets[i]).padStart(10, '0')} 00000 n \n`);
    }
    parts.push(`trailer << /Size 6 /Root 1 0 R >>\nstartxref\n${xrefPos}\n%%EOF`);
    const pdf = parts.join('');
    return {
      name: filename,
      size: pdf.length,
      type: 'application/pdf',
      dataUrl: 'data:application/pdf;base64,' + btoa(pdf),
    };
  }

  // ── SEED DATA ────────────────────────────────────────
  // private buildAssignments(): HomeworkAssignment[] {
  //   return [
  //     {
  //       id:1, title:'Numericals on Newton’s Laws of Motion', subject:'Physics',
  //       class_name:'Class XI – Science', section:'A', teacher_name:'Priya Sharma',
  //       assigned_date:'2026-09-05', due_date:'2026-09-12', due_time:'23:59', max_marks:20,
  //       instructions:'Solve questions 1–12 from Chapter 5. Show all working. Upload a single PDF.',
  //       documents:[ this.makePdfDocument('ch5-numericals.pdf', 'Chapter 5 - Newton\'s Laws', [
  //         'EduSmart International School  |  Class XI Science  |  Physics',
  //         'Solve questions 1-12. Show all working. Upload one PDF.',
  //         '1. A 2 kg block is pushed with 10 N. Find acceleration (ignore friction).',
  //         '2. A 5 kg body on a rough surface (mu = 0.2). Find friction.',
  //         '3-12. See textbook exercises 5.1 to 5.10.',
  //       ]) ],
  //       status:'Published', priority:'High', notify_on_save:true, created_at:'2026-09-05',
  //     },
  //     {
  //       id:2, title:'Essay: Climate Change and Our Future', subject:'English',
  //       class_name:'Class XI – Science', section:'A', teacher_name:'Anjali Menon',
  //       assigned_date:'2026-09-06', due_date:'2026-09-15', due_time:'18:00', max_marks:25,
  //       instructions:'Write a 400–500 word essay. Include an introduction, two body paragraphs, and a conclusion.',
  //       documents:[ this.makePdfDocument('essay-rubric.pdf', 'Essay Rubric - Climate Change', [
  //         'Length: 400-500 words. Structure: introduction, two body paragraphs, conclusion.',
  //         'Marks: Content 10 | Organisation 8 | Language 7. Total 25.',
  //         'Cite at least one real-world example (policy, city, or scientific finding).',
  //       ]) ],
  //       status:'Published', priority:'Normal', notify_on_save:true, created_at:'2026-09-06',
  //     },
  //     {
  //       id:3, title:'Quadratic Equations Worksheet', subject:'Mathematics',
  //       class_name:'Class IX', section:'A', teacher_name:'Rajesh Kumar',
  //       assigned_date:'2026-09-01', due_date:'2026-09-08', due_time:'23:59', max_marks:15,
  //       instructions:'Complete the worksheet. Attempt all word problems. Late submissions lose 2 marks per day.',
  //       documents:[ this.makePdfDocument('quad-ws-09.pdf', 'Quadratic Equations Worksheet', [
  //         'Class IX  |  Mathematics  |  15 marks',
  //         'Q1-Q6: Solve by factorisation. Q7-Q10: Word problems (time, area, numbers).',
  //         'Show steps. Late work: minus 2 marks per day.',
  //       ]) ],
  //       status:'Published', priority:'Normal', notify_on_save:true, created_at:'2026-09-01',
  //     },
  //     {
  //       id:4, title:'Salt Analysis Lab Report', subject:'Chemistry',
  //       class_name:'Class XI – Science', section:'A', teacher_name:'Meera Iyer',
  //       assigned_date:'2026-09-08', due_date:'2026-09-10', due_time:'16:00', max_marks:10,
  //       instructions:'Submit the observation table and inference for the given inorganic salt.',
  //       documents:[ this.makePdfDocument('lab-template.pdf', 'Salt Analysis Lab Template', [
  //         'Observation table: Preliminary tests | Dry heating | Flame test | Anion | Cation.',
  //         'Write inference for the given inorganic salt. Attach the completed table.',
  //       ]) ],
  //       status:'Published', priority:'High', notify_on_save:true, created_at:'2026-09-08',
  //     },
  //     {
  //       id:5, title:'Chapter 3 Reading Comprehension', subject:'Hindi',
  //       class_name:'Class IX', section:'A', teacher_name:'Sunita Verma',
  //       assigned_date:'2026-09-09', due_date:'2026-09-16', due_time:'23:59', max_marks:10,
  //       instructions:'Read the chapter and answer the short questions at the end. Draft — not yet assigned to students.',
  //       documents:[],
  //       status:'Draft', priority:'Low', notify_on_save:false, created_at:'2026-09-09',
  //     },
  //   ];
  // }

  // private buildSubmissions(): StudentHomework[] {
  //   const stamp = (d: string) => `${d}T10:15:00.000Z`;
  //   const blank = { file_name: '' as string, file: null as HomeworkDocument | null };
  //   const work = (name: string, student: string, note: string): Pick<StudentHomework, 'file_name' | 'file'> => ({
  //     file_name: name,
  //     file: this.makePdfDocument(name, student, [note, 'Submitted via EduSmart Homework.']),
  //   });
  //   return [
  //     { id:1,  homework_id:1, student_id:'S001', student_name:'Arjun Sharma',  roll_no:'11-A-01', class_name:'Class XI – Science', status:'In Progress', work_text:'Completed Q1–Q7. Working on friction problems.', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-08') },
  //     { id:2,  homework_id:1, student_id:'S002', student_name:'Priya Mehta',   roll_no:'11-A-02', class_name:'Class XI – Science', status:'Submitted',   work_text:'All 12 numericals attached.', ...work('priya-newton.pdf','Priya Mehta','Solutions to Newton numericals 1-12.'), submitted_at:stamp('2026-09-08'), marks_obtained:null, teacher_remark:'', student_remark:'Please check Q11.', last_updated:stamp('2026-09-08') },
  //     { id:3,  homework_id:1, student_id:'S006', student_name:'Kavita Yadav',  roll_no:'11-A-03', class_name:'Class XI – Science', status:'Assigned',    work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-05') },
  //     { id:4,  homework_id:1, student_id:'S013', student_name:'Meena Sharma',  roll_no:'11-A-04', class_name:'Class XI – Science', status:'Graded',      work_text:'PDF uploaded.', ...work('meena-newton.pdf','Meena Sharma','Newton laws working and answers.'), submitted_at:stamp('2026-09-07'), marks_obtained:18, teacher_remark:'Neat work. Recheck units in Q4.', student_remark:'', last_updated:stamp('2026-09-08') },
  //     { id:5,  homework_id:1, student_id:'S014', student_name:'Vikram Patel',  roll_no:'11-A-05', class_name:'Class XI – Science', status:'Assigned',    work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-05') },
  //     { id:6,  homework_id:2, student_id:'S001', student_name:'Arjun Sharma',  roll_no:'11-A-01', class_name:'Class XI – Science', status:'Assigned',    work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-06') },
  //     { id:7,  homework_id:2, student_id:'S002', student_name:'Priya Mehta',   roll_no:'11-A-02', class_name:'Class XI – Science', status:'In Progress', work_text:'Outline ready. Drafting body paragraphs.', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-08') },
  //     { id:8,  homework_id:2, student_id:'S006', student_name:'Kavita Yadav',  roll_no:'11-A-03', class_name:'Class XI – Science', status:'Assigned',    work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-06') },
  //     { id:9,  homework_id:2, student_id:'S013', student_name:'Meena Sharma',  roll_no:'11-A-04', class_name:'Class XI – Science', status:'Assigned',    work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-06') },
  //     { id:10, homework_id:2, student_id:'S014', student_name:'Vikram Patel',  roll_no:'11-A-05', class_name:'Class XI – Science', status:'Submitted',   work_text:'Essay of 480 words on climate action.', ...work('vikram-essay.pdf','Vikram Patel','Climate Change and Our Future - essay draft.'), submitted_at:stamp('2026-09-08'), marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-08') },
  //     { id:11, homework_id:3, student_id:'S003', student_name:'Rahul Kumar',   roll_no:'9-A-01',  class_name:'Class IX', status:'Late',       work_text:'Worksheet complete except last word problem.', ...work('rahul-quad.pdf','Rahul Kumar','Quadratic worksheet Q1-Q9.'), submitted_at:stamp('2026-09-09'), marks_obtained:null, teacher_remark:'', student_remark:'Sorry for the delay.', last_updated:stamp('2026-09-09') },
  //     { id:12, homework_id:3, student_id:'S005', student_name:'Vikram Singh',  roll_no:'9-A-02',  class_name:'Class IX', status:'Graded',     work_text:'All questions solved.', ...work('vikram-quad.pdf','Vikram Singh','All quadratic worksheet questions solved.'), submitted_at:stamp('2026-09-07'), marks_obtained:14, teacher_remark:'Excellent.', student_remark:'', last_updated:stamp('2026-09-08') },
  //     { id:13, homework_id:3, student_id:'S010', student_name:'Suresh Babu',   roll_no:'9-A-03',  class_name:'Class IX', status:'Assigned',   work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-01') },
  //     { id:14, homework_id:3, student_id:'S020', student_name:'Pooja Devi',    roll_no:'9-A-04',  class_name:'Class IX', status:'Returned',   work_text:'Attempted Q1–Q6.', ...work('pooja-quad.pdf','Pooja Devi','Attempted Q1-Q6 only.'), submitted_at:stamp('2026-09-06'), marks_obtained:null, teacher_remark:'Redo Q7–Q10 with proper steps.', student_remark:'', last_updated:stamp('2026-09-07') },
  //     { id:15, homework_id:4, student_id:'S001', student_name:'Arjun Sharma',  roll_no:'11-A-01', class_name:'Class XI – Science', status:'Assigned',    work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-08') },
  //     { id:16, homework_id:4, student_id:'S002', student_name:'Priya Mehta',   roll_no:'11-A-02', class_name:'Class XI – Science', status:'Assigned',    work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-08') },
  //     { id:17, homework_id:4, student_id:'S006', student_name:'Kavita Yadav',  roll_no:'11-A-03', class_name:'Class XI – Science', status:'Assigned',    work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-08') },
  //     { id:18, homework_id:4, student_id:'S013', student_name:'Meena Sharma',  roll_no:'11-A-04', class_name:'Class XI – Science', status:'Submitted',   work_text:'Observation table completed.', ...work('meena-lab.pdf','Meena Sharma','Salt analysis observation table and inference.'), submitted_at:stamp('2026-09-09'), marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-09') },
  //     { id:19, homework_id:4, student_id:'S014', student_name:'Vikram Patel',  roll_no:'11-A-05', class_name:'Class XI – Science', status:'Assigned',    work_text:'', ...blank, submitted_at:'', marks_obtained:null, teacher_remark:'', student_remark:'', last_updated:stamp('2026-09-08') },
  //   ];
  // }

  // private buildAlerts(): HomeworkAlert[] {
  //   return [
  //     { id:1, student_id:'S001', homework_id:4, title:'Salt Analysis Lab Report', text:'New homework assigned: “Salt Analysis Lab Report” (Chemistry) · due 10 Sep 2026.', time:'1 day ago', unread:true,  kind:'assigned', timestamp:'2026-09-08T08:10:00Z' },
  //     { id:2, student_id:'S001', homework_id:1, title:'Numericals on Newton’s Laws of Motion', text:'Reminder: “Numericals on Newton’s Laws of Motion” is due on 12 Sep 2026 at 11:59 PM.', time:'3 hours ago', unread:true, kind:'reminder', timestamp:'2026-09-09T01:00:00Z' },
  //     { id:3, student_id:'S001', homework_id:2, title:'Essay: Climate Change and Our Future', text:'New homework assigned: “Essay: Climate Change and Our Future” (English) · due 15 Sep 2026.', time:'3 days ago', unread:false, kind:'assigned', timestamp:'2026-09-06T09:00:00Z' },
  //     { id:4, student_id:'S002', homework_id:1, title:'Numericals on Newton’s Laws of Motion', text:'New homework assigned: “Numericals on Newton’s Laws of Motion” (Physics) · due 12 Sep 2026.', time:'4 days ago', unread:false, kind:'assigned', timestamp:'2026-09-05T09:00:00Z' },
  //     { id:5, student_id:'S013', homework_id:1, title:'Numericals on Newton’s Laws of Motion', text:'“Numericals on Newton’s Laws of Motion” has been graded: 18/20. Neat work. Recheck units in Q4.', time:'1 day ago', unread:true, kind:'graded', timestamp:'2026-09-08T14:20:00Z' },
  //     { id:6, student_id:'S020', homework_id:3, title:'Quadratic Equations Worksheet', text:'“Quadratic Equations Worksheet” was returned for revision. Redo Q7–Q10 with proper steps.', time:'2 days ago', unread:true, kind:'returned', timestamp:'2026-09-07T11:00:00Z' },
  //     { id:7, student_id:'S003', homework_id:3, title:'Quadratic Equations Worksheet', text:'Reminder: “Quadratic Equations Worksheet” is due on 08 Sep 2026 at 11:59 PM.', time:'2 days ago', unread:false, kind:'reminder', timestamp:'2026-09-07T08:00:00Z' },
  //     { id:8, student_id:'S010', homework_id:3, title:'Quadratic Equations Worksheet', text:'New homework assigned: “Quadratic Equations Worksheet” (Mathematics) · due 08 Sep 2026.', time:'8 days ago', unread:true, kind:'assigned', timestamp:'2026-09-01T09:00:00Z' },
  //   ];
  // }


  // ── ACTIONS ─────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    frmData["erp_key"] = this.user.erp_key;
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any, tf: boolean) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		// delete frmData.id;
    frmData["erp_key"] = this.user.erp_key;

    this.dataService.updateRecord(api_name, myid, frmData, tf).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
